import { useState } from 'react'
import { X, Plus, Trash2, Info, ExternalLink, GitBranch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import type {
  FieldDefinition, FieldType, SubFieldDefinition, SelectOption, LocalizedString, ValidationRule,
  ConditionalRule,
} from '../api/form-builder-api'
import { availableRulesFor } from '@/lib/form-engine'
import { FormulaBuilder } from './formula-builder'
import { cn } from '@/lib/utils/cn'
import {
  FIELD_TYPE_LABELS,
  SUB_FIELD_TYPES,
  readLocalized,
  sanitizeFieldKey,
  sanitizeOptionKey,
  makeNewValidationRule,
  VALIDATION_ACTION_LABELS,
} from './field-editor-helpers'
import { RuleRow } from './rule-row'
import { SortableOption } from './sortable-option'
import { nanoid } from 'nanoid'

// ─── Main component ───────────────────────────────────────────────────────────

interface FieldEditorPanelProps {
  field: FieldDefinition
  allFields: FieldDefinition[]
  conditionalRules?: ConditionalRule[]
  onChange: (updated: FieldDefinition) => void
  onClose: () => void
  /** Called when the user clicks an info box about a conditional rule affecting this field. */
  onOpenRule?: (ruleId: string) => void
  /** Field keys that exist in the currently-published version. These are locked
   *  from being renamed in the draft to avoid orphaning live submissions. */
  publishedFieldKeys?: Set<string>
}

export function FieldEditorPanel({
  field, allFields, conditionalRules = [], onChange, onClose, onOpenRule, publishedFieldKeys,
}: FieldEditorPanelProps) {
  const update = (patch: Partial<FieldDefinition>) => onChange({ ...field, ...patch })
  const updateTypeConfig = (patch: Record<string, unknown>) =>
    update({ type_config: { ...field.type_config, ...patch } })

  const sensors = useSensors(useSensor(PointerSensor))

  const validationRules: ValidationRule[] = field.validations ?? []
  const availableRules = availableRulesFor(field.field_type)

  const addValidationRule = () => {
    if (!availableRules.length) return
    const newRule = makeNewValidationRule(field.field_type, availableRules[0].type)
    update({ validations: [...validationRules, newRule] })
  }

  const updateValidationRule = (idx: number, updated: ValidationRule) => {
    const next = [...validationRules]
    next[idx] = updated
    update({ validations: next })
  }

  const removeValidationRule = (idx: number) => {
    update({ validations: validationRules.filter((_, i) => i !== idx) })
  }

  // ── Select options ────────────────────────────────────────────────────────

  const options: SelectOption[] = (field.type_config.options as SelectOption[] | undefined) ?? []

  const addOption = () => {
    const key = `opt_${nanoid(4).toLowerCase().replace(/-/g, '_')}`
    updateTypeConfig({
      options: [...options, { option_key: key, label: { fa: '' }, display_order: options.length }],
    })
  }

  const updateOption = (idx: number, patch: { option_key?: string; label?: string }) => {
    const next = [...options]
    const current = next[idx]
    const newLabel = patch.label !== undefined ? patch.label : current.label.fa
    // Auto-derive option_key from the label only while the key was never
    // edited (i.e., is still the auto-generated `opt_XXXX`) AND the user is
    // editing the label.
    const looksAutoGenerated = /^opt_[a-z0-9]{1,8}$/.test(current.option_key)
    let newKey = patch.option_key !== undefined ? patch.option_key : current.option_key
    if (patch.option_key === undefined && patch.label !== undefined && looksAutoGenerated) {
      const derived = sanitizeOptionKey(patch.label)
      if (derived) newKey = derived
    }
    next[idx] = { ...current, option_key: newKey, label: { fa: newLabel } }
    updateTypeConfig({ options: next })
  }

  const removeOption = (idx: number) => {
    updateTypeConfig({ options: options.filter((_, i) => i !== idx) })
  }

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = options.findIndex((o) => o.option_key === active.id)
    const newIdx = options.findIndex((o) => o.option_key === over.id)
    const reordered = arrayMove(options, oldIdx, newIdx).map((o, i) => ({ ...o, display_order: i }))
    updateTypeConfig({ options: reordered })
  }

  // ── Sub-fields ────────────────────────────────────────────────────────────

  const subFields: SubFieldDefinition[] =
    (field.type_config.sub_fields as SubFieldDefinition[] | undefined) ?? []

  const addSubField = () => {
    const key = `sub_${nanoid(4).toLowerCase().replace(/-/g, '_')}`
    const sub: SubFieldDefinition = {
      field_id: nanoid(),
      field_key: key,
      field_type: 'text',
      label: { fa: '' },
      is_required: false,
      type_config: {},
    }
    updateTypeConfig({ sub_fields: [...subFields, sub] })
  }

  const updateSubField = (idx: number, patch: Partial<SubFieldDefinition>) => {
    const next = [...subFields]
    next[idx] = { ...next[idx], ...patch }
    updateTypeConfig({ sub_fields: next })
  }

  const removeSubField = (idx: number) => {
    updateTypeConfig({ sub_fields: subFields.filter((_, i) => i !== idx) })
  }

  // ── Flags ─────────────────────────────────────────────────────────────────

  const isSelect = field.field_type === 'single_select' || field.field_type === 'multi_select'
  const isNumber = field.field_type === 'number'
  const isDate = field.field_type === 'date' || field.field_type === 'datetime'
  const isTime = field.field_type === 'time'
  const isRepeatable = field.field_type === 'repeatable_array'
  const isCalculated = field.field_type === 'calculated'
  const isBoolean = field.field_type === 'boolean'

  const tc = field.type_config

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div>
          <p className="text-xs font-medium text-foreground">ویرایش فیلد</p>
          <p className="text-[10px] text-muted-foreground">{FIELD_TYPE_LABELS[field.field_type] ?? field.field_type}</p>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      <Tabs defaultValue="general" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="shrink-0 rounded-none border-b border-border bg-transparent h-auto px-2 justify-start gap-0">
          <TabsTrigger
            value="general"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent text-xs px-3 py-2"
          >
            عمومی
          </TabsTrigger>
          <TabsTrigger
            value="validation"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent text-xs px-3 py-2"
          >
            <span className="flex items-center gap-1">
              اعتبارسنجی
              {validationRules.length > 0 && (
                <Badge className="h-4 min-w-4 text-[9px] px-1">{validationRules.length}</Badge>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          {/* ══════════════════════════════════════════════════════════════
              GENERAL TAB — identity, display config, options, sub-fields
              ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="general" className="p-4 space-y-4 mt-0">
            {/* Label */}
            <div className="space-y-1.5">
              <Label className="text-xs">عنوان فیلد</Label>
              <Input
                value={field.label.fa}
                onChange={(e) => update({ label: { ...field.label, fa: e.target.value } })}
                placeholder="عنوان فارسی فیلد"
                className="h-8 text-sm"
              />
            </div>

            {/* Field key — directly editable, sanitized, uniqueness-checked. */}
            {(() => {
              const sameKey = allFields.filter((f) => f.field_id !== field.field_id && f.field_key === field.field_key)
              const empty = !field.field_key
              const isPublished = publishedFieldKeys ? publishedFieldKeys.has(field.field_key) : false
              const duplicate = sameKey.length > 0
              const invalid = empty || duplicate
              return (
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    کلید فیلد (field key)
                    {isPublished && (
                      <span className="text-[9px] text-yellow-400">قفل پس از انتشار</span>
                    )}
                  </Label>
                  <Input
                    value={field.field_key}
                    onChange={(e) => update({ field_key: sanitizeFieldKey(e.target.value) })}
                    placeholder="patient_age"
                    className={cn('h-8 text-xs font-mono', invalid && 'border-destructive')}
                    dir="ltr"
                    disabled={isPublished}
                    aria-invalid={invalid || undefined}
                  />
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    فقط حروف انگلیسی، عدد و _. باید در فرم منحصربه‌فرد باشد. این کلید در submissions و قوانین شرطی استفاده می‌شود.
                  </p>
                  {empty && <p className="text-[10px] text-destructive">کلید نمی‌تواند خالی باشد</p>}
                  {duplicate && (
                    <p className="text-[10px] text-destructive">
                      این کلید با فیلد دیگری تکراری است
                    </p>
                  )}
                </div>
              )
            })()}

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs">راهنمای فیلد (اختیاری)</Label>
              <Textarea
                value={field.description?.fa ?? ''}
                onChange={(e) =>
                  update({ description: e.target.value ? { fa: e.target.value } : undefined })
                }
                placeholder="توضیح کوتاه برای کاربر..."
                rows={2}
                className="text-sm"
              />
            </div>

            {/* Default visibility */}
            <div className="space-y-1.5">
              <Label className="text-xs">نمایش پیش‌فرض</Label>
              <Select
                value={field.default_visibility ?? 'visible'}
                onValueChange={(v) => update({ default_visibility: v as 'visible' | 'hidden' })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible">نمایان</SelectItem>
                  <SelectItem value="hidden">پنهان (نمایش با قانون)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Type-specific DISPLAY config ─────────────────────────── */}

            {field.field_type === 'textarea' && (
              <div className="space-y-1.5">
                <Label className="text-xs">تعداد ردیف‌های نمایش</Label>
                <Input
                  type="number"
                  value={(tc.rows_hint as number | undefined) ?? ''}
                  onChange={(e) => updateTypeConfig({ rows_hint: e.target.value ? Number(e.target.value) : undefined })}
                  className="h-7 text-xs w-24"
                  dir="ltr"
                />
              </div>
            )}

            {isNumber && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">واحد</Label>
                  <Input
                    value={(tc.unit_label as string | undefined) ?? ''}
                    onChange={(e) => updateTypeConfig({ unit_label: e.target.value || undefined })}
                    placeholder="kg، cm، ..."
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">نمایش اعشار</Label>
                  <Input
                    type="number"
                    value={(tc.decimal_places as number | undefined) ?? ''}
                    onChange={(e) => updateTypeConfig({ decimal_places: e.target.value ? Number(e.target.value) : undefined })}
                    className="h-7 text-xs"
                    dir="ltr"
                  />
                </div>
              </div>
            )}

            {isDate && (
              <div className="space-y-1.5">
                <Label className="text-xs">تقویم</Label>
                <Select
                  value={(tc.calendar_mode as string | undefined) ?? 'jalali'}
                  onValueChange={(v) => updateTypeConfig({ calendar_mode: v })}
                >
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jalali">شمسی</SelectItem>
                    <SelectItem value="gregorian">میلادی</SelectItem>
                    <SelectItem value="both">هر دو</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isTime && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={Boolean(tc.seconds_visible)}
                  onCheckedChange={(v) => updateTypeConfig({ seconds_visible: Boolean(v) })}
                />
                <span className="text-xs text-muted-foreground">نمایش ثانیه</span>
              </label>
            )}

            {isBoolean && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">برچسب بله</Label>
                  <Input
                    value={readLocalized(tc.true_label) ?? ''}
                    onChange={(e) => updateTypeConfig({ true_label: e.target.value })}
                    placeholder="بله"
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">برچسب خیر</Label>
                  <Input
                    value={readLocalized(tc.false_label) ?? ''}
                    onChange={(e) => updateTypeConfig({ false_label: e.target.value })}
                    placeholder="خیر"
                    className="h-7 text-xs"
                  />
                </div>
              </div>
            )}

            {field.field_type === 'image' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={Boolean(tc.image_preview)}
                  onCheckedChange={(v) => updateTypeConfig({ image_preview: Boolean(v) })}
                />
                <span className="text-xs text-muted-foreground">پیش‌نمایش تصویر</span>
              </label>
            )}

            {isCalculated && (
              <div className="space-y-3">
                <FormulaBuilder
                  formula={(tc.formula as string | undefined) ?? ''}
                  onChange={(next) => updateTypeConfig({ formula: next })}
                  availableFields={allFields.filter((f) => f.field_id !== field.field_id)}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">نوع نتیجه</Label>
                    <Select
                      value={(tc.result_type as string | undefined) ?? 'number'}
                      onValueChange={(v) => updateTypeConfig({ result_type: v })}
                    >
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="number">عدد</SelectItem>
                        <SelectItem value="text">متن</SelectItem>
                        <SelectItem value="boolean">بله/خیر</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">اعشار</Label>
                    <Input
                      type="number"
                      value={(tc.decimal_places as number | undefined) ?? ''}
                      onChange={(e) => updateTypeConfig({ decimal_places: e.target.value ? Number(e.target.value) : undefined })}
                      className="h-7 text-xs"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>
            )}

            {isSelect && (
              <div className="space-y-2">
                <Label className="text-xs">گزینه‌ها</Label>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOptionDragEnd}>
                  <SortableContext items={options.map((o) => o.option_key)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {options.map((opt, idx) => (
                        <SortableOption
                          key={opt.option_key + ':' + idx}
                          opt={opt}
                          idx={idx}
                          allKeys={options.map((o) => o.option_key)}
                          onChange={(patch) => updateOption(idx, patch)}
                          onRemove={() => removeOption(idx)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <Button variant="outline" size="sm" className="w-full h-7 text-xs gap-1" onClick={addOption}>
                  <Plus className="w-3 h-3" />
                  افزودن گزینه
                </Button>
              </div>
            )}

            {isRepeatable && (
              <div className="space-y-2">
                <Label className="text-xs">فیلدهای زیرمجموعه</Label>
                {subFields.map((sub, idx) => (
                  <div key={sub.field_id} className="border border-border rounded-lg p-2.5 space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={sub.label.fa}
                        onChange={(e) => updateSubField(idx, { label: { fa: e.target.value } })}
                        placeholder="عنوان زیرفیلد"
                        className="h-6 text-xs flex-1"
                      />
                      <Select
                        value={sub.field_type}
                        onValueChange={(v) => updateSubField(idx, { field_type: v as SubFieldDefinition['field_type'] })}
                      >
                        <SelectTrigger className="h-6 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SUB_FIELD_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeSubField(idx)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={sub.field_key}
                        onChange={(e) =>
                          updateSubField(idx, {
                            field_key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                          })
                        }
                        placeholder="field_key"
                        className="h-6 text-[10px] font-mono flex-1"
                        dir="ltr"
                      />
                      <label className="flex items-center gap-1 cursor-pointer">
                        <Checkbox
                          checked={sub.is_required}
                          onCheckedChange={(v) => updateSubField(idx, { is_required: Boolean(v) })}
                          className="w-3.5 h-3.5"
                        />
                        <span className="text-[10px] text-muted-foreground">اجباری</span>
                      </label>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="w-full h-7 text-xs gap-1" onClick={addSubField}>
                  <Plus className="w-3 h-3" />
                  افزودن زیرفیلد
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════
              VALIDATION TAB — required + unified rule list + conditional info
              All validation-related settings for the field live here.
              ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="validation" className="p-4 mt-0 space-y-3">
            {/* Required (base — may be flipped at runtime by conditional rules) */}
            <div className="rounded-md border border-border bg-muted/20 px-3 py-2.5 space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={field.is_required}
                  onCheckedChange={(v) => update({ is_required: Boolean(v) })}
                />
                <span className="text-xs font-medium text-foreground">اجباری بودن فیلد</span>
              </label>
              <p className="text-[10px] text-muted-foreground leading-relaxed ps-6">
                «اجباری بودن» بخشی از سیستم اعتبارسنجی است. اگر هیچ مقداری وارد نشود، ارسال فرم متوقف می‌شود.
                این پایه ممکن است توسط قوانین شرطی (در تب «قوانین») تغییر کند.
              </p>
            </div>

            {/* Conditional rules that affect this field's validation/visibility — read-only info */}
            {(() => {
              const affecting = conditionalRules.filter((r) =>
                r.actions.some((a) => a.target_field_key === field.field_key && VALIDATION_ACTION_LABELS[a.type])
              )
              if (affecting.length === 0) return null
              return (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <GitBranch className="w-3 h-3" />
                    این فیلد تحت تأثیر {affecting.length.toLocaleString('fa-IR')} قانون شرطی است
                  </p>
                  {affecting.map((rule) => {
                    const relevantActions = rule.actions.filter(
                      (a) => a.target_field_key === field.field_key && VALIDATION_ACTION_LABELS[a.type]
                    )
                    return (
                      <button
                        key={rule.rule_id}
                        type="button"
                        onClick={() => onOpenRule?.(rule.rule_id)}
                        disabled={!onOpenRule}
                        className="w-full text-start rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-2 hover:bg-sky-500/15 transition-colors disabled:cursor-default group"
                      >
                        <div className="flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0 space-y-1">
                            <p className="text-[11px] font-medium text-sky-300 truncate">
                              {rule.description || 'قانون بدون نام'}
                              {!rule.is_enabled && <span className="ms-1 text-muted-foreground">(غیرفعال)</span>}
                            </p>
                            <ul className="text-[10px] text-muted-foreground space-y-0.5">
                              {relevantActions.map((a, i) => (
                                <li key={i}>• {VALIDATION_ACTION_LABELS[a.type].label}</li>
                              ))}
                            </ul>
                          </div>
                          {onOpenRule && (
                            <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 mt-0.5" />
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            })()}

            <div className="text-[11px] text-muted-foreground leading-relaxed bg-muted/30 border border-border rounded-md p-2.5">
              قوانین اعتبارسنجی این فیلد. هر قانون می‌تواند <strong>بلوک‌کننده</strong> باشد (ارسال متوقف می‌شود)
              یا <strong>هشدار</strong> (کاربر برای ادامه ارسال باید توجیه بالینی وارد کند).
            </div>

            {availableRules.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                برای این نوع فیلد قانون اعتبارسنجی قابل تعریف نیست.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">قوانین</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs gap-1"
                    onClick={addValidationRule}
                  >
                    <Plus className="w-3 h-3" />
                    افزودن قانون
                  </Button>
                </div>
                {validationRules.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                    هنوز قانون اعتبارسنجی تعریف نشده.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {validationRules.map((rule, idx) => (
                      <RuleRow
                        key={rule.rule_id}
                        rule={rule}
                        fieldType={field.field_type}
                        allFields={allFields}
                        onChange={(r) => updateValidationRule(idx, r)}
                        onDelete={() => removeValidationRule(idx)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}
